"use client";

import { Link } from "@/navigation";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslations } from "next-intl";
import { useParams } from "next/navigation";
import { ProductCategoriesListQuery } from "@/gql/graphql";

type CategoryEdge = ProductCategoriesListQuery["categories"]["edges"][0];

const CategoryItem = ({
	locale,
	item,
	level = 0,
	currentSlug,
	expandedCategories,
	toggleCategory,
}: {
	locale?: string;
	item: CategoryEdge;
	level?: number;
	currentSlug: string;
	expandedCategories: string[];
	toggleCategory: (id: string) => void;
}) => {
	const hasChildren = item.node.children?.edges?.length > 0;
	const isExpanded = expandedCategories.includes(item.node.id);
	const isActive = item.node.slug === currentSlug;

	return (
		<div className="relative">
			<div
				className={`group flex items-center justify-between cursor-pointer py-3 px-5 transition-all duration-200 hover:bg-mainColor/20
					${level === 0 ? "bg-gray-100" : "bg-white"}
					${isActive && level === 0 ? "bg-gray-100" : ""}
					${level === 1 ? "border-b border-gray-200" : ""}`}
				style={level > 0 ? { paddingLeft: `${level * 20 + 20}px` } : {}}
			>
				<Link
					href={`/products/${item.node.slug}`}
					className={`flex-1 transition-colors duration-200 hover:text-mainColor
						${level === 0 
							? `text-base font-medium ${isActive ? "!text-mainColor" : "text-gray-800"}` 
							: `text-base ${isActive ? "!text-mainColor font-medium" : "text-gray-600"}`
						}`}
				>
					{item?.node?.translation?.name || item?.node?.name}
				</Link>
				{hasChildren && (
					<button
						onClick={(e) => {
							e.preventDefault();
							toggleCategory(item.node.id);
						}}
						className="flex h-7 w-7 items-center justify-center text-gray-600 hover:text-gray-800 transition-colors ml-3 text-xl font-bold"
					>
						{isExpanded ? "-" : "+"}
					</button>
				)}
			</div>

			{hasChildren && (
				<div
					className={`overflow-hidden transition-all duration-500 ease-in-out
						${isExpanded ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0"}`}
					style={{
						transitionProperty: 'max-height, opacity',
						transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
					}}
				>
					<div className="bg-white">
						{item.node.children?.edges.map((child: CategoryEdge) => (
							<CategoryItem
								key={child.node.id}
								item={child}
								level={level + 1}
								currentSlug={currentSlug}
								expandedCategories={expandedCategories}
								toggleCategory={toggleCategory}
							/>
						))}
					</div>
				</div>
			)}
		</div>
	);
};

type CategoriesProps = ProductCategoriesListQuery["categories"];

const findParentCategoryIds = (
	categories: CategoryEdge[] | undefined,
	targetSlug: string,
	parentIds: string[] = [],
): string[] => {
	if (!categories) return [];

	return categories.reduce((acc, category) => {
		if (category.node.slug === targetSlug) return parentIds;

		if (category.node.children?.edges?.length) {
			const result = findParentCategoryIds(category.node.children.edges, targetSlug, [
				...parentIds,
				category.node.id,
			]);
			if (result.length) return result;
		}
		return acc;
	}, [] as string[]);
};

const getSiblingCategories = (categories: CategoryEdge[] | undefined, targetId: string): string[] => {
	if (!categories) return [];

	const findParent = (
		items: CategoryEdge[],
		id: string,
	): { parent: CategoryEdge | null; siblings: string[] } => {
		for (const item of items) {
			// 如果是顶级类别
			if (item.node.id === id) {
				return {
					parent: null,
					siblings: items.map((cat) => cat.node.id).filter((catId) => catId !== id),
				};
			}

			// 在子类别中查找
			if (item.node.children?.edges) {
				for (const child of item.node.children.edges) {
					if (child.node.id === id) {
						return {
							parent: item,
							siblings: item.node.children.edges.map((cat) => cat.node.id).filter((catId) => catId !== id),
						};
					}
				}
				const result = findParent(item.node.children.edges, id);
				if (result.parent || result.siblings.length) return result;
			}
		}
		return { parent: null, siblings: [] };
	};

	return findParent(categories, targetId).siblings;
};

const Categories = ({ locale, categoriesData }: { locale?: string; categoriesData: CategoriesProps }) => {
	const t = useTranslations();
	const { slug: currentSlug } = useParams();
	const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

	useEffect(() => {
		if (currentSlug && categoriesData) {
			// 获取当前分类的父分类ID
			const parentIds = findParentCategoryIds(categoriesData.edges, currentSlug as string);
			// 合并已展开的分类，不要覆盖用户手动展开的分类
			setExpandedCategories((prev) => {
				// 只添加新的父分类ID，保留之前已展开的分类
				const newIds = parentIds.filter(id => !prev.includes(id));
				return [...prev, ...newIds];
			});
		} else if (!currentSlug) {
			// 如果回到一级分类页面，不清空已展开的分类
			// 保持用户手动展开的状态
		}
	}, [locale, currentSlug, categoriesData]);

	const toggleCategory = useCallback(
		(categoryId: string) => {
			setExpandedCategories((prev) =>
				prev.includes(categoryId)
					? prev.filter((id) => id !== categoryId)
					: [...prev, categoryId]
			);
		},
		[],
	);
    const sortedCategories = useMemo(() => {
      if (!categoriesData?.edges) return [];
      return [...categoriesData.edges].sort((a:any,b:any) => {
        if (!a.node.sortNumber && b.node.sortNumber) return 1;
        if (a.node.sortNumber && !b.node.sortNumber) return -1;
        if (!a.node.sortNumber && !b.node.sortNumber) return 0;
        return a.node.sortNumber - b.node.sortNumber;
      });
    }, [categoriesData]);
  

	return (
		<div className="overflow-hidden border border-gray-200 bg-white shadow-md w-full">
			<div className="bg-mainColor px-6 py-4">
				<h2 className="text-xl font-bold text-white flex items-center gap-2">
					<i className="ri-folder-line text-xl"></i>
					{t("blog.Categories")}
				</h2>
			</div>
			<div className="list-cate">
				{sortedCategories.length>0&&sortedCategories.map((item) => (
					<CategoryItem
						key={item.node.id}
						item={item}
						currentSlug={currentSlug as string}
						expandedCategories={expandedCategories}
						toggleCategory={toggleCategory}
					/>
				))}
			</div>
		</div>
	);
};

export default Categories;
